import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { AppColors } from '../theme/colors';
import { signInWithAccessLink } from '../services/accessLinkService';
import { AuthService } from '../services/authService';

/**
 * 専用URL / QRコードからのログイン画面（/q/:token）
 *
 * 会社IDとパスワードの入力が投稿の妨げになっているという要望を受けた導線。
 * 利用者が行うのはQRを読み取ることだけで、この画面は結果を表示するのみ。
 *
 * URL に含まれるのは自前で発行した合言葉であり、カスタムトークンではない。
 * サーバー(redeemAccessLink)が合言葉を検証してカスタムトークンを返す。
 *
 * 設計書: レフリアメモ/認証の一本化について.md（11章）
 */
export const QrLoginPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  // React の開発モードでは useEffect が2回実行されるため、多重ログインを防ぐ
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      if (!token) {
        navigate('/scan-required', { replace: true });
        return;
      }

      try {
        await AuthService.setPersistence();

        // 管理者としてログインしていた場合の状態が残らないようにする
        localStorage.removeItem('adminUserId');

        const { companyId } = await signInWithAccessLink(token);

        // ログアウトしたときの行き先を判断するために、QRから入ったことを残す。
        // QRの利用者は会社IDもパスワードも知らないため、ログイン画面へ戻すと
        // 行き止まりになる。
        localStorage.setItem('loginMethod', 'qr');

        // 端末登録（会社IDログインと同じ扱いにする。失敗してもログインは継続）
        try {
          await AuthService.registerDeviceManually(companyId);
        } catch (deviceError) {
          console.error('[QRログイン] デバイス登録エラー:', deviceError);
        }

        navigate('/home', { replace: true });
      } catch (err: any) {
        // 案内は /scan-required に一本化する。ここで理由を出し分けても、
        // 利用者にできるのは管理者へ問い合わせることだけで変わらない。
        // 調査のためにエラーの内容はコンソールに残す。
        console.error('[QRログイン] エラー:', err);
        navigate('/scan-required', { replace: true });
      }
    };

    run();
  }, [token, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Box component="img" src="/icon.svg" alt="LiaVoice" sx={{ width: 96, height: 96, mb: 2 }} />
      <Typography
        sx={{
          fontSize: '1.4rem',
          fontWeight: 800,
          color: AppColors.primary,
          letterSpacing: '1.0px',
          fontFamily: '"Nunito", "Noto Sans JP", sans-serif',
          mb: 3,
        }}
      >
        LiaVoice
      </Typography>

      <CircularProgress sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary">
        ログインしています...
      </Typography>
    </Box>
  );
};

export default QrLoginPage;
